// ExamUnionBuySeries.jsx  
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const ExamUnionBuySeriesListComponent = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();

  const [passMasters, setPassesMasters] = useState([]);
  const [myPasses, setMyPasses] = useState([]);
  const [highlightStates, setHighlightStates] = useState({});

  // 🔹 Fetch all passes (no login restriction if public)
  const fetchPassesMasters = useCallback(() => {
    const config = {
      url: "/examunionpassmasters",
      method: "get",
      headers: { 
        Authorization: authState.token,
        "Content-Type": "application/json"
      },
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && Array.isArray(response.examunionpassmasters)) {
          setPassesMasters(response.examunionpassmasters);
        }
      })
      .catch(error => console.error("Error fetching PassesMasters:", error));
  }, [authState.token, fetchData]);

  useEffect(() => {
    fetchPassesMasters();
  }, [fetchPassesMasters]);

  // 🔹 Fetch user's purchased passes (only if logged in)
  const fetchMyPasses = useCallback(() => {
    if (!authState.isLoggedIn) return;

    const config = {
      url: "/myexamunionpasses",
      method: "get",
      headers: { Authorization: authState.token }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && Array.isArray(response.myexamunionpasses)) {
          setMyPasses(response.myexamunionpasses);
        }
      })
      .catch(error => console.error("Error fetching MyPasses:", error));
  }, [authState.isLoggedIn, authState.token, fetchData]);

  useEffect(() => {
    fetchMyPasses();
  }, [fetchMyPasses]);

  // 🔹 Sort passes (safe + optimized)
  const processedPasses = React.useMemo(() => {
    return [...(passMasters || [])]
      .sort((a, b) => (a.displayIndex || 0) - (b.displayIndex || 0));
  }, [passMasters]);

  // 🔹 Optimize purchased lookup (O(1))
  const purchasedPassIds = React.useMemo(() => {
    return new Set((myPasses || []).map(p => p.passId));
  }, [myPasses]);

  const handlePassDetailsClick = (passId, pass, hasPurchased) => {
    navigate('/buying-passdetails', {
      state: { passId, passData: pass, hasPurchased }
    });
  };

  return (
    <div className="flex flex-col items-center w-full px-3 [&_p]:text-white">
      {loading ? (
        <div className="flex justify-center items-center py-10 text-white text-lg">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span className="ml-2">ExamUnion</span>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className='w-full p-2'>
            <div className="w-full flex flex-col items-center text-white">
              <p className="text-lg lg:text-xl py-2 px-4 text-center font-semibold">
                Test Series Passes {processedPasses.length}
              </p>
            </div>
          </div>

          {processedPasses.map((pass, index) => (
            <div
              key={pass.passCode || pass._id}
              className="w-full bg-gray-900/40 rounded-lg px-3 py-2 mt-1 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-xs lg:text-sm text-gray-400 w-6">
                  {index + 1}.
                </span>

                <h2 className="text-xs lg:text-sm text-white">
                  {pass.passTitle}
                </h2>
              </div>

              <div className="flex items-center gap-2">
                {purchasedPassIds.has(pass._id) ? (
                  <button
                    onClick={() => handlePassDetailsClick(pass._id, pass, true)}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded-md"
                  >
                    Explore
                  </button>
                ) : (
                <button
                  onClick={() => handlePassDetailsClick(pass._id, pass, false)}
                  className="text-xs bg-purple-600 text-white px-3 py-1 rounded-md min-w-[70px]"
                >
                  Details
                </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const ExamUnionBuySeries = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black py-4 px-3 [&_p]:text-white">
      <h2 className="text-center text-yellow-200 font-bold text-xl">
        ExamUnion Buy Test Series Pass
      </h2>

      {/* Info Card */}
      <div className="w-full px-2 py-2">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-600 via-blue-700 to-indigo-800 shadow-lg">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-400 opacity-20 blur-3xl rounded-full"></div>

          <div className="relative p-4 text-white">
            <p className="text-center text-sm">
              📚 Get <span className="font-semibold text-yellow-300">ExamUnion Series</span> to unlock:
            </p>

            <ul className="grid grid-cols-2 lg:grid-cols-4 text-xs mt-2 gap-1 text-center">
              <li>✅ PYQ Practice</li>
              <li>✅ Practice Series</li>
              <li>✅ AITS Mock Tests</li>
              <li>✅ Special Boost Sets</li>
            </ul>

            <p className="text-center text-xs mt-2 text-gray-200">
              ⚡ Earn Pocket Money & Win Scholarships
            </p>
          </div>
        </div>
      </div>

      {/* Estimator */}
      <div className="bg-cyan-500 px-2 py-2">
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => navigate("/examunion-earning-estimator")} className="btn">Test Series</button>
          <button onClick={() => navigate("/orderflow-earning-estimator")} className="btn">Digital</button>
          <button onClick={() => navigate("/orderflow-earning-estimator")} className="btn">Marketplace</button>
        </div>
      </div>

      {/* List */}
      <div className="mt-2 w-full flex justify-center">
        <ExamUnionBuySeriesListComponent />
      </div>
    </div>
  );
};

export default ExamUnionBuySeries;